import {createContext, createElement, useContext, useMemo, useSyncExternalStore, type ReactNode} from 'react';
import {IDLE_VIEW, type ReliefView, type VideoReliefController} from './videoReliefController';

const controllers = new Map<string, VideoReliefController>();
const detachers = new Map<string, () => void>();
const listeners = new Set<() => void>();

const notify = (): void => listeners.forEach(listener => listener());

const subscribe = (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

export const registerRelief = (key: string, controller: VideoReliefController): void => {
    detachers.get(key)?.();
    controllers.set(key, controller);
    detachers.set(key, controller.subscribe(notify));
    notify();
};

export const releaseRelief = (key: string, controller: VideoReliefController): void => {
    if (controllers.get(key) !== controller) {
        return;
    }
    detachers.get(key)?.();
    detachers.delete(key);
    controllers.delete(key);
    notify();
};

const ReliefScopeContext = createContext<string | undefined>(undefined);

export const ReliefScope = ({scope, children}: {scope: string; children: ReactNode}) =>
    createElement(ReliefScopeContext.Provider, {value: scope}, children);

export const useReliefView = (): ReliefView => {
    const key = useContext(ReliefScopeContext);
    return useSyncExternalStore(subscribe, () => (key ? controllers.get(key)?.getSnapshot() : undefined) ?? IDLE_VIEW);
};

export interface ReliefActions {
    readonly accept: () => void;
    readonly dismiss: () => void;
    readonly resume: () => void;
}

export const useReliefActions = (): ReliefActions => {
    const key = useContext(ReliefScopeContext);
    return useMemo(
        () => ({
            accept: () => (key ? controllers.get(key)?.accept() : undefined),
            dismiss: () => (key ? controllers.get(key)?.dismiss() : undefined),
            resume: () => (key ? controllers.get(key)?.resumeNow() : undefined),
        }),
        [key],
    );
};
